import { peopleSearchableSpec, type PeopleSearchableKind, type PeopleSearchableOp } from '@customerio/hydra-ui-api';
import { ATTRIBUTE_CONDITION_TYPE } from '../../codecs/attribute-condition';
import type { GroupableFamily } from '../../operator-groups';
import { operatorsInFamily } from '../../operators';
import { ConditionType, type PeopleSearchConditionType } from './condition-types';

export type PeopleSearchableContract = {
  readonly combinators: { readonly max_depth: number };
  readonly leaves: {
    readonly [Kind in PeopleSearchableKind]?: {
      readonly inverse: boolean;
      readonly ops?: { readonly [op: string]: { readonly values: string } };
    };
  };
};

export type PeopleSearchableKindWithOps = {
  [Kind in PeopleSearchableKind]: PeopleSearchableOp<Kind> extends never ? never : Kind;
}[PeopleSearchableKind];

type PeopleSearchableAnyOp = PeopleSearchableOp<PeopleSearchableKind>;

// Exhaustive, so an operator services adds fails `tsc` until it has a family.
const FAMILY_BY_SPEC_OPERATOR = {
  eq: 'equality',
  exists: 'existence',
  one_of: 'oneOf',
  email_valid: 'email',
} as const satisfies Record<PeopleSearchableAnyOp, GroupableFamily>;

function allowedOperators(kind: PeopleSearchableKindWithOps, contract: PeopleSearchableContract): Set<string> {
  const leaf = contract.leaves[kind];
  const ops = Object.keys(leaf?.ops ?? {});
  return new Set(leaf?.inverse === true ? [...ops, ...ops.map((op) => `!${op}`)] : ops);
}

// A family's dropdown shows its `!` negations too, so a leaf without `inverse` offers no family.
export function peopleSearchableOperatorFamilies<Kind extends PeopleSearchableKindWithOps>(
  kind: Kind,
  parity: readonly PeopleSearchableOp<Kind>[],
  contract: PeopleSearchableContract = peopleSearchableSpec,
): GroupableFamily[] {
  const allowed = allowedOperators(kind, contract);
  const families = new Set<GroupableFamily>(parity.map((op: PeopleSearchableAnyOp) => FAMILY_BY_SPEC_OPERATOR[op]));
  return [...families].filter((family) => operatorsInFamily(family).every((operator) => allowed.has(operator.value)));
}

export const SPEC_KINDS_BY_CONDITION_TYPE = {
  [ATTRIBUTE_CONDITION_TYPE]: ['attribute'],
  [ConditionType.Consent]: ['named_bitmap'],
  [ConditionType.ConsentState]: ['attribute', 'named_bitmap'],
  [ConditionType.DeviceAttribute]: ['device_attribute'],
  [ConditionType.Event]: ['named_bitmap'],
  [ConditionType.Message]: ['named_bitmap'],
  [ConditionType.NewsletterRecipients]: ['newsletter_recipients'],
  [ConditionType.OptOut]: ['named_bitmap'],
  [ConditionType.SegAttr]: ['seg_attr'],
  [ConditionType.SegmentsNegatable]: ['segment'],
} as const satisfies Record<PeopleSearchConditionType, readonly PeopleSearchableKind[]>;

export function isPeopleSearchableConditionType(
  type: PeopleSearchConditionType,
  contract: PeopleSearchableContract = peopleSearchableSpec,
): boolean {
  return SPEC_KINDS_BY_CONDITION_TYPE[type].every((kind) => contract.leaves[kind] != null);
}

export function peopleSearchableConditionTypes<Type extends PeopleSearchConditionType>(
  types: readonly Type[],
  contract: PeopleSearchableContract = peopleSearchableSpec,
): Type[] {
  return types.filter((type) => isPeopleSearchableConditionType(type, contract));
}

export function peopleSearchableMaxDepth(
  surfaceMaxDepth: number,
  contract: PeopleSearchableContract = peopleSearchableSpec,
): number {
  return Math.min(surfaceMaxDepth, contract.combinators.max_depth);
}

export function advertisedWithoutRows(
  rowTypes: readonly PeopleSearchConditionType[],
  contract: PeopleSearchableContract = peopleSearchableSpec,
): string[] {
  const written = new Set<string>(rowTypes.flatMap((type) => SPEC_KINDS_BY_CONDITION_TYPE[type]));
  return Object.keys(contract.leaves)
    .filter((kind) => !written.has(kind))
    .sort();
}
